import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Icon from 'components/Icon';
import { IOption } from 'components/Select/Select';
import { EditSeriesRow } from './styles';
import Input from '../Input';
import SelectWithPopover from '../Select/SelectWithPopover';
import { Draggable } from 'react-beautiful-dnd';
import { ITags, ITagView } from 'store/providers/providerTypes';
import {
  IChoiceView,
  IMultipleAnswerField,
  IQuestionsView,
  ISeries,
  ISurveyView,
} from 'store/survey/surveyTypes';
import i18n from '../../i18n';
import ColorPicker from './RelatedResponses/ColorPicker';

interface ISeriesEditorProps {
  tags: ITags;
  index: number;
  series: ISeries;
  survey?: ISurveyView;
  onChangeItem: (seriesIndex: number, series: ISeries) => void;
  onRemoveItem: (seriesIndex: number) => void;
  hasScoreFilter: boolean;
}

export const responseListQuestionFields = [
  'submitDate',
  'score',
  'relatedUser',
  'userId',
  'effectiveDate',
];

class SeriesEditor extends React.Component<ISeriesEditorProps> {
  constructor(props: ISeriesEditorProps) {
    super(props);
  }

  questionFields = [
    { value: 'submitDate', label: i18n.t('submitDate', { ns: 'survey' }) },
    { value: 'score', label: i18n.t('score', { ns: 'survey' }) },
    { value: 'relatedUser', label: i18n.t('relatedUser', { ns: 'survey' }) },
    { value: 'userId', label: i18n.t('userId', { ns: 'survey' }) },
    {
      value: 'effectiveDate',
      label: i18n.t('effectiveDate', { ns: 'survey' }),
    },
  ];

  handleChangeQuestionValue = (seriesId: number) => (values: IOption[]) => {
    const { series: seriesFromProps, onChangeItem } = this.props;
    const series = cloneDeep(seriesFromProps);
    series.questionId = values.reduce((current, item) => {
      if (item.value === 0) return '0';
      if (current === '0') return '0';
      return current + (current ? ',' : '') + item.value;
    }, '');
    if (series.questionId === '0' || series.questionId.includes(',')) {
      series.subQuestionId = null;
      series.splitOnValues = false;
    } else {
      series.includeTotal = false;
      if (
        !series.questionId ||
        (!series.questionId.includes('@score') &&
          !['multiChoice', 'grid'].includes(
            this.props.survey?.questionMap![series.questionId].type
          ))
      )
        series.splitOnValues = false;
      if (
        !series.questionId ||
        (!series.questionId.includes('@score') &&
          !['multiAnswer'].includes(
            this.props.survey?.questionMap![series.questionId].type
          ))
      )
        series.splitOnFieldValues = undefined;
    }
    onChangeItem(seriesId, series);
  };
  handleChangeSelect =
    (seriesId: number) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const name = e.target.name;
      const value = e.target.value;
      const { series: seriesFromProps, onChangeItem } = this.props;
      const series = cloneDeep(seriesFromProps);
      if (name === 'questionId') {
        series.subQuestionId = null;
      }
      series[name] = value === 'null' ? null : value;
      onChangeItem(seriesId, series);
    };
  handleChangeSelectOption = (values: IOption[]) => {
    const { series: seriesFromProps, onChangeItem, index } = this.props;
    const series = cloneDeep(seriesFromProps);
    series.subQuestionId =
      values[0].value === 'null' ? null : (values[0].value as string);
    onChangeItem(index, series);
  };
  handleChangeInput =
    (seriesId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.checked;
      const { series: seriesFromProps, onChangeItem } = this.props;
      const series = cloneDeep(seriesFromProps);
      series[name] = value;
      onChangeItem(seriesId, series);
    };

  handleChangeSeriesValue = (seriesId: number) => (values: IOption[]) => {
    const { series: seriesFromProps, onChangeItem } = this.props;
    const series = cloneDeep(seriesFromProps);
    series.tagId = null;
    series.tagIds = values.map((it) => it.value as number);
    onChangeItem(seriesId, series);
  };

  handelRemoveSeries = () => {
    const { index, onRemoveItem } = this.props;
    onRemoveItem(index);
  };

  handleChangeSeries = (
    type: string,
    selectedType?: string | null | string[]
  ) => {
    const { series: seriesFromProps, onChangeItem } = this.props;
    const series = cloneDeep(seriesFromProps);
    series[type] = selectedType as any;
    onChangeItem(this.props.index, series);
  };

  handleChangeChartType = (selectedOption: IOption[]) => {
    this.handleChangeSeries('chartType', selectedOption[0].value.toString());
  };

  handleChangeMetrics = (selectedOption: IOption[]) => {
    this.handleChangeSeries('metric', selectedOption[0].value.toString());
  };
  handleChangeOrder = (selectedOption: IOption[]) => {
    const selectedOrder = selectedOption[0].value.toString();
    this.handleChangeSeries('order', selectedOrder);
  };

  handleChangeStep = (selectedOption: IOption[]) => {
    this.handleChangeSeries(
      'subQuestionId',
      selectedOption[0].value.toString()
    );
  };

  handleChangeSplitByStep = (selectedOption: IOption[]) => {
    this.handleChangeSeries(
      'splitOnFieldValues',
      selectedOption[0].value.toString()
    );
  };
  handleChangeValueFilter = (selectedOption: IOption[]) => {
    this.handleChangeSeries(
      'valueFilter',
      selectedOption.map((it) => it.value.toString())
    );
  };

  handleChangeSubQuestion = (selectedOption: IOption[]) => {
    this.handleChangeSeries(
      'splitOnFieldValues',
      selectedOption[0].value.toString()
    );
  };

  render() {
    const { series, survey, tags, hasScoreFilter } = this.props;
    let question: IQuestionsView | undefined;
    let questionsTags: IOption[] = [];
    if (
      series.type === 'question' &&
      series.questionId &&
      !series.questionId?.toString().includes(',') &&
      series.questionId !== '0' &&
      survey
    ) {
      question =
        survey.questionMap?.[
          Number(
            series.questionId.includes('@score')
              ? series.questionId.split('@score')[0]
              : series.questionId
          )
        ].data;
    } else if (series.type === 'tag') {
      if (survey) {
        questionsTags = Object.values(survey?.questionMap || {}).reduce(
          (tagsList: IOption[], qItem) => {
            const qTags = qItem.data.tags;
            if (qTags) {
              qTags.forEach((tag: ITagView) => {
                if (!tagsList.find((item) => item.value === tag.id)) {
                  const option: IOption = {
                    label: tag.text,
                    value: tag.id,
                  };
                  tagsList.push(option);
                }
              });
            }
            return tagsList;
          },
          []
        );
      } else {
        questionsTags = Object.values(tags.data).reduce(
          (tagsList: IOption[], tag) => {
            const option: IOption = {
              label: tag.text,
              value: tag.id,
            };
            tagsList.push(option);
            return tagsList;
          },
          []
        );
      }
    }
    let questionOptions: IOption[] = survey
      ? Object.values(survey?.questionMap || {})
          .filter((q) => {
            if (!q.data?.deleted) {
              if (series.metric === 'responseList') {
                if (
                  ['text', 'multipleChoice', 'indicator', 'file'].includes(
                    q.data.type
                  )
                )
                  return true;
                else return false;
              } else {
                if (
                  q.data?.type === 'grid' ||
                  q.data?.type === 'indicator' ||
                  q.data?.type === 'multipleAnswer'
                ) {
                  return true;
                } else if (q.data?.type === 'multipleChoice') {
                  if (
                    series.metric === 'count' ||
                    series.metric === 'percentageOfCount'
                  ) {
                    return true;
                  }
                  return q.data.choiceList.find(
                    (choice: IChoiceView) => !!choice.score
                  );
                }
              }
            }
            return false;
          })
          .sort((a, b) => a.data.order - b.data.order)
          .reduce((acc: IOption[], q) => {
            if (
              q.data.type === 'multipleChoice' &&
              series.metric === 'responseList'
            ) {
              acc = [
                ...acc,
                { value: q.data.id, label: q.data.title },
                {
                  value: `${q.data.id}@score`,
                  label: `${q.data.title} (${i18n.t('score', { ns: 'survey' })})`,
                },
              ];
            } else {
              acc.push({ value: q.data.id, label: q.data.title });
            }
            return acc;
          }, [])
      : [];

    if (series.metric === 'responseList') {
      questionOptions = [...questionOptions, ...this.questionFields];
    }

    if (
      (series.questionId === '0' || !series.questionId) &&
      series.type === 'question'
    ) {
      questionOptions = [
        { value: 0, label: i18n.t('allQuestionsSplit', { ns: 'survey' }) },
        ...questionOptions,
      ];
    }
    const chartTypeOptions: IOption[] = [
      { value: 'default', label: i18n.t('default', { ns: 'survey' }) },
      { value: 'line', label: i18n.t('line', { ns: 'survey' }) },
      { value: 'bar', label: i18n.t('bar', { ns: 'survey' }) },
      { value: 'barRev', label: i18n.t('barRev', { ns: 'survey' }) },
      { value: 'stack', label: i18n.t('stack', { ns: 'survey' }) },
      { value: 'stackRev', label: i18n.t('stackRev', { ns: 'survey' }) },
      { value: 'pie', label: i18n.t('pie', { ns: 'survey' }) },
      { value: 'table', label: i18n.t('table', { ns: 'survey' }) },
      { value: 'tableRev', label: i18n.t('tableRev', { ns: 'survey' }) },
      { value: 'iranMap', label: i18n.t('iranMap', { ns: 'survey' }) },
    ];
    let metricOptions: IOption[] = [
      { value: 'totalAvg', label: i18n.t('totalAvg', { ns: 'survey' }) },
      {
        value: 'percentageOfTotalAvg',
        label: i18n.t('percentageOfTotalAvg', { ns: 'survey' }),
      },
      { value: 'count', label: i18n.t('count', { ns: 'survey' }) },
      {
        value: 'percentageOfCount',
        label: i18n.t('percentageOfCount', { ns: 'survey' }),
      },
      { value: 'sum', label: i18n.t('sum', { ns: 'survey' }) },
    ];
    if (series.type === 'question') {
      metricOptions.push({
        value: 'responseList',
        label: i18n.t('textResponseList', { ns: 'survey' }),
      });
    }

    if (hasScoreFilter) {
      const scoreFilterOption =
        series.type === 'question' ? 'responseList' : 'count';
      metricOptions = metricOptions.filter(
        (el: IOption) => scoreFilterOption === el.value.toString()
      );
    }
    const orderOptions: IOption[] = [
      { value: 'ValueAsc', label: i18n.t('ValueAsc', { ns: 'survey' }) },
      {
        value: 'ValueDesc',
        label: i18n.t('ValueDesc', { ns: 'survey' }),
      },
      { value: 'Default', label: i18n.t('default', { ns: 'survey' }) },
    ];
    let subQuestionOptions: IOption[] = [
      { value: 'null', label: i18n.t('totalScore', { ns: 'survey' }) },
      {
        value: '',
        label: i18n.t('allSubQuestionsSplit', { ns: 'survey' }),
      },
    ];
    if (question && question.type === 'grid' && question.parts) {
      subQuestionOptions = [
        ...subQuestionOptions,
        ...question.parts.map((part: IChoiceView) => ({
          value: part.value,
          label: part.text,
        })),
      ];
    }
    let stepOptions: IOption[] = [
      {
        value: 'null',
        label: i18n.t('totalScore', { ns: 'survey' }),
      },
    ];
    if (question && question.type === 'multipleAnswer') {
      stepOptions = [
        ...stepOptions,
        ...question?.questionFields
          .filter(
            (part: IMultipleAnswerField) => part.withValue || part.options
          )
          .map((part: IMultipleAnswerField) => ({
            value: part.key as string,
            label: part.title as string,
          })),
      ];
    }
    let splitByStepOptions: IOption[] = [
      { value: '', label: i18n.t('noSplit', { ns: 'survey' }) },
    ];
    if (question && question.type === 'multipleAnswer') {
      splitByStepOptions = [
        ...splitByStepOptions,
        ...question.questionFields
          .filter(
            (part: IMultipleAnswerField) =>
              part.withValue ||
              (part.options && part.key !== series.subQuestionId)
          )
          .map((part: IMultipleAnswerField) => ({
            value: part.key as string,
            label: part.title as string,
          })),
      ];
    }
    return (
      <Draggable
        draggableId={this.props.index.toString()}
        index={this.props.index}
      >
        {(provided) => {
          return (
            <div
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              ref={provided.innerRef}
            >
              <EditSeriesRow>
                <div>
                  {series.type === 'all' && <Icon type="voicemail" />}
                  {series.type === 'question' && <Icon type="help-circle" />}
                  {series.type === 'tag' && <Icon type="tag" />}
                  {series.type === 'list' && <Icon type="list" />}
                </div>
                <div>
                  <label htmlFor="chartType">
                    {i18n.t('chartType', { ns: 'survey' }) as string}
                  </label>
                  <SelectWithPopover
                    onChangeValues={this.handleChangeChartType}
                    options={chartTypeOptions}
                    values={chartTypeOptions.filter(
                      (it: IOption) =>
                        it.value.toString() ===
                        (this.props.series.chartType || 'default')
                    )}
                    multiChoice={false}
                    isRequired={true}
                    allowRemove={false}
                  />
                </div>
                {this.props.series.metric !== 'responseList' && (
                  <>
                    <ColorPicker
                      color={series.color || ''}
                      onChangeColor={(color: string) =>
                        this.handleChangeSeries('color', color)
                      }
                      hideLabel={false}
                      size="normal"
                    />
                  </>
                )}
                <div>
                  <label htmlFor="metric">
                    {i18n.t('metric', { ns: 'survey' }) as string}
                  </label>
                  <SelectWithPopover
                    onChangeValues={this.handleChangeMetrics}
                    options={metricOptions}
                    values={metricOptions.filter(
                      (it: IOption) =>
                        it.value.toString() === this.props.series.metric
                    )}
                    multiChoice={false}
                    isRequired={true}
                    allowRemove={false}
                  />
                </div>
                {this.props.series.metric !== 'responseList' && (
                  <>
                    <div>
                      <label htmlFor="order">
                        {i18n.t('order', { ns: 'survey' }) as string}
                      </label>
                      <SelectWithPopover
                        onChangeValues={this.handleChangeOrder}
                        options={orderOptions}
                        values={orderOptions.filter(
                          (it: IOption) =>
                            it.value.toString() ===
                            (this.props.series.order || 'Default')
                        )}
                        multiChoice={false}
                        isRequired={true}
                        allowRemove={false}
                      />
                    </div>
                  </>
                )}
                {series.type === 'question' && (
                  <>
                    {survey && (
                      <>
                        <div>
                          <SelectWithPopover
                            placement="bottom"
                            onChangeValues={this.handleChangeQuestionValue(
                              this.props.index
                            )}
                            options={questionOptions}
                            label={i18n.t('question', { ns: 'survey' })}
                            multiChoice={series.questionId !== '0'}
                            allowRemove={true}
                            values={
                              series.questionId
                                ?.toString()
                                ?.split(',')
                                .filter((e) => !!e || e === '0')
                                .map(
                                  (it) =>
                                    questionOptions.find(
                                      (o) =>
                                        o.value === Number(it) || o.value === it
                                    ) ||
                                    this.questionFields.find(
                                      (el) => el.value === it
                                    ) || {
                                      value: it,
                                      label: it,
                                    }
                                ) ?? []
                            }
                          />
                        </div>
                        {question && question.type === 'grid' && (
                          <>
                            <div>
                              <label htmlFor="subQuestionId">
                                {
                                  i18n.t('subQuestion', {
                                    ns: 'survey',
                                  }) as string
                                }
                              </label>
                              <SelectWithPopover
                                onChangeValues={this.handleChangeSelectOption}
                                options={subQuestionOptions}
                                values={subQuestionOptions.filter(
                                  (it: IOption) => {
                                    return (
                                      it.value.toString() ===
                                      `${this.props.series.subQuestionId}`
                                    );
                                  }
                                )}
                                multiChoice={false}
                                isRequired={true}
                              />
                              {/*<select*/}
                              {/*  className="browser-default"*/}
                              {/*  name="subQuestionId"*/}
                              {/*  id="subQuestionId"*/}
                              {/*  onChange={this.handleChangeSelect(this.props.index)}*/}
                              {/*  value={*/}
                              {/*    series.subQuestionId !== null*/}
                              {/*      ? series.subQuestionId*/}
                              {/*      : 'null'*/}
                              {/*  }*/}
                              {/*>*/}
                              {/*  <option value="null">*/}
                              {/*    {i18n.t('totalScore', { ns: 'survey' }) as string}*/}
                              {/*  </option>*/}
                              {/*  <option value="">*/}
                              {/*    {*/}
                              {/*      i18n.t('allSubQuestionsSplit', {*/}
                              {/*        ns: 'survey',*/}
                              {/*      }) as string*/}
                              {/*    }*/}
                              {/*  </option>*/}
                              {/*  {question.parts &&*/}
                              {/*    question.parts.map((part: IChoiceView) => (*/}
                              {/*      <option value={part.value} key={part.value}>*/}
                              {/*        {part.text}*/}
                              {/*      </option>*/}
                              {/*    ))}*/}
                              {/*</select>*/}
                            </div>
                            {series.subQuestionId !== null && (
                              <div>
                                <Input
                                  id={'splitOnValues' + this.props.index}
                                  type="checkbox"
                                  name="splitOnValues"
                                  label={
                                    i18n.t('splitOnValues', {
                                      ns: 'survey',
                                    }) as string
                                  }
                                  checked={series.splitOnValues}
                                  onChange={this.handleChangeInput(
                                    this.props.index
                                  )}
                                />
                              </div>
                            )}
                          </>
                        )}
                        {question && question.type === 'multipleChoice' && (
                          <>
                            {
                              <div>
                                <Input
                                  id={'splitOnValues' + this.props.index}
                                  type="checkbox"
                                  name="splitOnValues"
                                  label={
                                    i18n.t('splitOnValues', {
                                      ns: 'survey',
                                    }) as string
                                  }
                                  checked={series.splitOnValues}
                                  onChange={this.handleChangeInput(
                                    this.props.index
                                  )}
                                />
                              </div>
                            }
                          </>
                        )}
                        {question && question.type === 'multipleAnswer' && (
                          <>
                            <div>
                              <label htmlFor="subQuestionId">
                                {
                                  i18n.t('subQuestion', {
                                    ns: 'survey',
                                  }) as string
                                }
                              </label>
                              <SelectWithPopover
                                onChangeValues={this.handleChangeStep}
                                options={stepOptions}
                                values={stepOptions.filter(
                                  (it: IOption) =>
                                    it.value.toString() ===
                                    this.props.series.subQuestionId
                                )}
                                multiChoice={false}
                                allowRemove={false}
                                isRequired={true}
                              />
                              {/*<select*/}
                              {/*  className="browser-default"*/}
                              {/*  name="subQuestionId"*/}
                              {/*  id="subQuestionId"*/}
                              {/*  onChange={this.handleChangeSelect(this.props.index)}*/}
                              {/*  value={*/}
                              {/*    series.subQuestionId !== null*/}
                              {/*      ? series.subQuestionId*/}
                              {/*      : 'null'*/}
                              {/*  }*/}
                              {/*>*/}
                              {/*  <option value="null">*/}
                              {/*    {i18n.t('totalScore', { ns: 'survey' })}*/}
                              {/*  </option>*/}
                              {/*  {question.questionFields &&*/}
                              {/*    question.questionFields.map(*/}
                              {/*      (part: IMultipleAnswerField) =>*/}
                              {/*        part.withValue || part.options ? (*/}
                              {/*          <option value={part.key!} key={part.key}>*/}
                              {/*            {part.title}*/}
                              {/*          </option>*/}
                              {/*        ) : (*/}
                              {/*          <> </>*/}
                              {/*        )*/}
                              {/*    )}*/}
                              {/*</select>*/}
                            </div>
                            {series.subQuestionId &&
                              question.questionFields &&
                              question.questionFields.find(
                                (it) => it.key === series.subQuestionId
                              )?.options?.length && (
                                <div>
                                  <Input
                                    id={'splitOnValues' + this.props.index}
                                    type="checkbox"
                                    name="splitOnValues"
                                    label={
                                      i18n.t('splitOnValues', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                    checked={series.splitOnValues}
                                    onChange={this.handleChangeInput(
                                      this.props.index
                                    )}
                                  />
                                </div>
                              )}
                            <div>
                              <label htmlFor="subQuestionId">
                                {
                                  i18n.t('splitOnFieldValues', {
                                    ns: 'survey',
                                  }) as string
                                }
                              </label>
                              <SelectWithPopover
                                onChangeValues={this.handleChangeSplitByStep}
                                options={splitByStepOptions}
                                values={splitByStepOptions.filter(
                                  (it: IOption) =>
                                    it.value.toString() ===
                                    this.props.series.splitOnFieldValues
                                )}
                                multiChoice={false}
                                allowRemove={false}
                                isRequired={true}
                              />
                              {/*<select*/}
                              {/*  className="browser-default"*/}
                              {/*  name="splitOnFieldValues"*/}
                              {/*  id="splitOnFieldValues"*/}
                              {/*  onChange={this.handleChangeSelect(this.props.index)}*/}
                              {/*  value={*/}
                              {/*    series.splitOnFieldValues !== null*/}
                              {/*      ? series.splitOnFieldValues*/}
                              {/*      : 'null'*/}
                              {/*  }*/}
                              {/*>*/}
                              {/*  <option value="null">*/}
                              {/*    {i18n.t('noSplit', { ns: 'survey' })}*/}
                              {/*  </option>*/}
                              {/*  {question.questionFields &&*/}
                              {/*    question.questionFields.map(*/}
                              {/*      (part: IMultipleAnswerField) =>*/}
                              {/*        part.withValue ||*/}
                              {/*        (part.options &&*/}
                              {/*          part.key !== series.subQuestionId) ? (*/}
                              {/*          <option value={part.key!} key={part.key}>*/}
                              {/*            {part.title}*/}
                              {/*          </option>*/}
                              {/*        ) : (*/}
                              {/*          <> </>*/}
                              {/*        )*/}
                              {/*    )}*/}
                              {/*</select>*/}
                            </div>
                          </>
                        )}
                        {question && question.type === 'indicator' && (
                          <div>
                            <label htmlFor="subQuestionId">
                              {
                                i18n.t('subQuestion', {
                                  ns: 'survey',
                                }) as string
                              }
                            </label>

                            <SelectWithPopover
                              onChangeValues={(values: IOption[]) => {
                                const {
                                  series: seriesFromProps,
                                  onChangeItem,
                                } = this.props;
                                const series = cloneDeep(seriesFromProps);
                                series.subQuestionId =
                                  values[0].value === 'null'
                                    ? null
                                    : (values[0].value as string);
                                onChangeItem(this.props.index, series);
                              }}
                              options={[
                                {
                                  value: 'null',
                                  label: i18n.t('totalScore', {
                                    ns: 'survey',
                                  }) as string,
                                },
                                ...(question.denominator
                                  ? [
                                      {
                                        value: 'numerator',
                                        label: question.numerator,
                                      },
                                      {
                                        value: 'denominator',
                                        label: question.denominator,
                                      },
                                    ]
                                  : []),
                              ]}
                              values={[
                                {
                                  value:
                                    series.subQuestionId !== null
                                      ? (series.subQuestionId as string)
                                      : 'null',
                                  label:
                                    series.subQuestionId === 'numerator'
                                      ? question.numerator || ''
                                      : series.subQuestionId === 'denominator'
                                        ? question.denominator || ''
                                        : (i18n.t('totalScore', {
                                            ns: 'survey',
                                          }) as string),
                                },
                              ]}
                              multiChoice={false}
                              isRequired={true}
                              allowRemove={false}
                            />
                          </div>
                        )}
                        {series.metric !== 'responseList' &&
                          ((!question && series.questionId) ||
                            (question?.type === 'grid' &&
                              series.subQuestionId !== null)) && (
                            <div>
                              <Input
                                id={'includeTotal' + this.props.index}
                                type="checkbox"
                                name="includeTotal"
                                label={
                                  i18n.t('includeTotal', {
                                    ns: 'survey',
                                  }) as string
                                }
                                checked={series.includeTotal}
                                onChange={this.handleChangeInput(
                                  this.props.index
                                )}
                              />
                            </div>
                          )}
                      </>
                    )}
                    {!survey && (
                      <SelectWithPopover
                        options={[]}
                        values={series.valueFilter?.map((it: string) => ({
                          value: it,
                          label: it,
                        }))}
                        allowRemove={true}
                        multiChoice
                        letAddChip
                        onChangeValues={this.handleChangeValueFilter}
                      />
                    )}
                  </>
                )}
                {series.type === 'tag' && (
                  <>
                    <SelectWithPopover
                      placement="right"
                      label={i18n.t('questionsTags', { ns: 'survey' })}
                      values={
                        series.tagId
                          ? [
                              {
                                label: tags.data[series.tagId].text,
                                value: series.tagId,
                              },
                            ]
                          : (series.tagIds?.map((it) => ({
                              label: tags.data[it].text,
                              value: it,
                            })) ?? [])
                      }
                      multiChoice={true}
                      allowRemove={false}
                      options={questionsTags}
                      onChangeValues={this.handleChangeSeriesValue(
                        this.props.index
                      )}
                      containerStyle={{ flexGrow: 1 }}
                    />
                    <div>
                      <Input
                        id={'split' + this.props.index}
                        type="checkbox"
                        name="split"
                        label={i18n.t('split', { ns: 'survey' }) as string}
                        checked={series.split}
                        onChange={this.handleChangeInput(this.props.index)}
                      />
                    </div>
                    {series.tagIds?.length ? (
                      <div>
                        <Input
                          id={'includeTotal' + this.props.index}
                          type="checkbox"
                          name="includeTotal"
                          label={
                            i18n.t('includeTotal', { ns: 'survey' }) as string
                          }
                          checked={series.includeTotal}
                          onChange={this.handleChangeInput(this.props.index)}
                        />
                      </div>
                    ) : null}
                  </>
                )}
                <div style={{ marginInlineStart: 'auto' }}>
                  <Icon
                    type={'trash'}
                    onClick={this.handelRemoveSeries}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </EditSeriesRow>
            </div>
          );
        }}
      </Draggable>
    );
  }
}

export default SeriesEditor;
